import api from '@/api';

export default {
  namespaced: true,
  state: {
    employees: [],
    currentEmployee: null,
    isLoading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 10,
      from: 0,
      to: 0
    },
    filters: {
      search: '',
      gender: null
    }
  },
  mutations: {
    SET_EMPLOYEES(state, employees) { state.employees = employees; },
    SET_CURRENT_EMPLOYEE(state, employee) { state.currentEmployee = employee; },
    SET_LOADING(state, isLoading) { state.isLoading = isLoading; },
    SET_ERROR(state, error) { state.error = error; },
    SET_PAGINATION(state, pagination) { state.pagination = pagination; },
    SET_FILTERS(state, filters) { state.filters = filters; },
    ADD_EMPLOYEE(state, employee) { state.employees.unshift(employee); },
    UPDATE_EMPLOYEE(state, updated) {
      const index = state.employees.findIndex(e => e.id === updated.id);
      if (index !== -1) state.employees.splice(index, 1, updated);
    },
    DELETE_EMPLOYEE(state, id) { state.employees = state.employees.filter(e => e.id !== id); }
  },
  actions: {
    async fetchEmployees({ commit, state }, { page = 1, per_page = 10 } = {}) {
      commit('SET_LOADING', true);
      try {
        const response = await api.get('/employees', {
          params: {
            page: page || state.pagination.currentPage,
            per_page: per_page || state.pagination.perPage,
            search: state.filters.search,
            gender: state.filters.gender
          }
        });

        commit('SET_EMPLOYEES', response.data.data);
        commit('SET_PAGINATION', {
          currentPage: response.data.meta.current_page,
          totalPages: response.data.meta.last_page,
          totalItems: response.data.meta.total,
          perPage: response.data.meta.per_page,
          from: response.data.meta.from,
          to: response.data.meta.to
        });
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    fetchEmployee({ commit }, id) {
      commit('SET_LOADING', true);
      return api.get(`/employees/${id}`)
        .then(response => commit('SET_CURRENT_EMPLOYEE', response.data.data))
        .catch(error => {
          commit('SET_ERROR', error.response?.data?.message || error.message);
          throw error;
        })
        .finally(() => commit('SET_LOADING', false));
    },

    createEmployee({ commit }, formData) {
      commit('SET_LOADING', true);
      return api.post('/employees', formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Accept': 'application/json' }
      })
        .then(response => {
          commit('ADD_EMPLOYEE', response.data.data);
          return response.data;
        })
        .catch(error => {
          commit('SET_ERROR', error.response?.data?.message || error.message);
          throw error;
        })
        .finally(() => commit('SET_LOADING', false));
    },

    updateEmployee({ commit }, { id, employeeData }) {
      commit('SET_LOADING', true);
      return api.post(`/employees/${id}`, employeeData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Accept': 'application/json' }
      })
        .then(response => {
          commit('UPDATE_EMPLOYEE', response.data.data);
          return response.data;
        })
        .catch(error => {
          commit('SET_ERROR', error.response?.data?.message || error.message);
          throw error;
        })
        .finally(() => commit('SET_LOADING', false));
    },

    deleteEmployee({ commit }, id) {
      commit('SET_LOADING', true);
      return api.delete(`/employees/${id}`)
        .then(() => {
          commit('DELETE_EMPLOYEE', id);
          return Promise.resolve();
        })
        .catch(error => {
          commit('SET_ERROR', error.response?.data?.message || error.message);
          return Promise.reject(error);
        })
        .finally(() => commit('SET_LOADING', false));
    },

    setFilters({ commit, dispatch }, filters) {
      commit('SET_FILTERS', filters);
      dispatch('fetchEmployees');
    }
  },
  getters: {
    employees: state => state.employees,
    employee: state => state.currentEmployee,
    isLoading: state => state.isLoading,
    error: state => state.error,
    pagination: state => state.pagination,
  }
};
